/**
 * ContractedServicesCard — reusable card that shows every active specialty
 * service on a property's contract. Used on:
 *   • StaffDashboard.js       — so field contractors know what they're
 *                                obligated to deliver on each site.
 *   • PropertyManagerDashboard.js — so PMs confirm they're getting what they
 *                                   pay for (parity with contractor view).
 *   • CompanyManagerDashboard.js  — read-only preview inside property drilldowns.
 *
 * IMPORTANT: This is a display-only component. All slugs are still the raw
 * backend keys (`bulk_removal`, `pressure_washing`, …) — see
 * `lib/specialtyServices.js` for the slug → label translation.
 */
import { useEffect, useState } from 'react';
import { ClipboardCheck, CheckCircle2, Clock, ShieldCheck, ChevronRight, Activity, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  activeSpecialtyServices,
  canonicalCollection,
  frequencyLabel,
} from '../lib/specialtyServices';
import ServiceHistoryDrawer from './ServiceHistoryDrawer';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPLIANCE_STYLES = {
  green: { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', color: '#34d399', label: 'On-track' },
  amber: { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', color: '#fbbf24', label: 'At risk' },
  red:   { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  color: '#f87171', label: 'Behind' },
  na:    { bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.35)', color: '#94a3b8', label: 'Review' },
};

const PACKAGE_TAGLINES = {
  'Essential Collection':     'Core valet trash + hallway pickup service.',
  'Professional Collection':  'Essential + expanded compliance reporting.',
  'Elite Collection':         'Professional + monthly bulk removal.',
  'Signature Collection':     'Elite + annual pressure washing service.',
  'Noble Empire Executive Collection':     'Signature + dumpster treatment + window cleaning.',
  'Noble Empire Presidential Collection':  'Full-stack — every specialty service, weekly amenity care.',
};

/**
 * @param {object}  property     — full property object (must contain
 *                                 service_policies + package_type).
 * @param {string}  [viewerRole] — 'staff' | 'property_manager' | 'admin'
 *                                 controls the copy on the card header.
 * @param {string}  [propertyName] — override for the header name (falls back
 *                                   to property.property_name).
 * @param {boolean} [compact]    — if true, hides descriptions to fit dense
 *                                 dashboards.
 */
export default function ContractedServicesCard({
  property,
  viewerRole = 'staff',
  propertyName,
  compact = false,
}) {
  const [historySlug, setHistorySlug] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loggingSlug, setLoggingSlug] = useState(null);
  const services = activeSpecialtyServices(property);

  const canLogCompletion = ['staff', 'supervisor', 'crew_leader', 'agent'].includes(viewerRole);

  const refetchCompliance = async () => {
    const pid = property?.property_id;
    if (!pid) return;
    try {
      const res = await fetch(
        `${API}/api/ops/properties/${pid}/compliance?days=30`,
        { credentials: 'include' },
      );
      if (res.ok) setCompliance((await res.json())?.services || {});
    } catch (_) { /* silent */ }
  };

  const logCompletion = async (slug, label) => {
    if (!property?.property_id) return;
    const notes = window.prompt(
      `Log a ${label} completion on ${property.property_name || 'this property'}?\n\nOptional notes (e.g. building #, crew, anything relevant):`,
      '',
    );
    if (notes === null) return;   // cancelled
    setLoggingSlug(slug);
    try {
      const res = await fetch(
        `${API}/api/ops/properties/${property.property_id}/log-completion`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ service_type: slug, notes: notes || null }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to log completion');
        return;
      }
      toast.success(`${label} logged — SLA score refreshed.`);
      await refetchCompliance();
    } catch (_) {
      toast.error('Network error');
    } finally {
      setLoggingSlug(null);
    }
  };

  // Rolling 30-day compliance score for every active service. Fires once per
  // property on mount + whenever the property changes. Fail-soft: if the
  // endpoint 4xx/5xx (or the viewer lacks permission), we simply hide the
  // score chip and everything else keeps working.
  useEffect(() => {
    const pid = property?.property_id;
    if (!pid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/ops/properties/${pid}/compliance?days=30`,
          { credentials: 'include' },
        );
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setCompliance(j?.services || {});
      } catch (_) { /* silent — chip is best-effort UX */ }
    })();
    return () => { cancelled = true; };
  }, [property?.property_id]);
  // Resolve legacy tier names ("Premium Package", "Platinum Package", …) to
  // their canonical Collection name at display time — the underlying DB
  // string stays intact so nothing downstream breaks.
  const rawTier = property?.package_type || property?.service_package || '';
  const tier = canonicalCollection(rawTier);
  const tierTagline = PACKAGE_TAGLINES[tier] || '';
  const displayName = propertyName || property?.property_name || 'This property';

  const header =
    viewerRole === 'staff'
      ? 'Contracted Services'
      : viewerRole === 'property_manager'
        ? 'Your Contracted Services'
        : 'Contracted Services';

  const subhead =
    viewerRole === 'staff'
      ? `What ${displayName} expects from you today`
      : viewerRole === 'property_manager'
        ? `Services included in your ${tier || 'contract'} — this is what NECS is obligated to deliver.`
        : `Services active on this property's contract`;

  return (
    <div
      className="rounded-xl border p-5"
      style={{
        backgroundColor: '#000000',
        borderColor: '#C9A962',
        boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
      }}
      data-testid="contracted-services-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" style={{ color: '#C9A962' }} />
            <h3
              className="text-lg font-bold"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                color: '#C9A962',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {header}
            </h3>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1">{subhead}</p>
        </div>
        {tier && (
          <div className="text-right shrink-0">
            <div
              className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
              style={{
                backgroundColor: 'rgba(201, 169, 98, 0.15)',
                border: '1px solid #C9A962',
                color: '#C9A962',
              }}
              data-testid="contracted-services-tier-badge"
            >
              {tier}
            </div>
            {tierTagline && !compact && (
              <p className="text-[10px] text-zinc-500 mt-1 max-w-[220px]">{tierTagline}</p>
            )}
          </div>
        )}
      </div>

      {services.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-zinc-700 bg-zinc-900/40 p-4 text-center"
          data-testid="contracted-services-empty"
        >
          <ShieldCheck className="h-6 w-6 mx-auto text-zinc-600 mb-2" />
          <p className="text-sm text-zinc-300 font-semibold">
            Only standard valet trash service is active.
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {viewerRole === 'property_manager'
              ? 'Upgrade to Elite or higher to add specialty services like bulk removal + pressure washing.'
              : 'No specialty services on this contract yet.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="contracted-services-list">
          {services.map((svc) => {
            const cScore = compliance?.[svc.slug];
            const cStatus = cScore?.status || null;
            const cStyle = cStatus ? COMPLIANCE_STYLES[cStatus === 'n/a' ? 'na' : cStatus] : null;
            const cPct = cScore?.score != null ? Math.round(cScore.score * 100) : null;
            return (
            <li
              key={svc.slug}
              className="rounded-md"
              data-testid={`contracted-service-${svc.slug}`}
            >
              <button
                type="button"
                onClick={() => property?.property_id && setHistorySlug(svc.slug)}
                disabled={!property?.property_id}
                className="w-full text-left flex items-start gap-3 rounded-md px-3 py-2.5 transition hover:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-[#C9A962] disabled:opacity-70 disabled:cursor-default"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid={`contracted-service-open-${svc.slug}`}
                title={
                  property?.property_id
                    ? `Open ${svc.label} service history for the last 30 days`
                    : svc.label
                }
              >
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-white">{svc.label}</span>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#34d399',
                      }}
                      data-testid={`contracted-service-status-${svc.slug}`}
                    >
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: '#34d399' }}
                      />
                      Status: Active
                    </span>
                    {svc.policy?.frequency && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-zinc-400">
                        <Clock className="h-3 w-3" />
                        {frequencyLabel(svc.policy.frequency)}
                      </span>
                    )}
                    {cStyle && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: cStyle.bg,
                          border: `1px solid ${cStyle.border}`,
                          color: cStyle.color,
                        }}
                        data-testid={`contracted-service-compliance-${svc.slug}`}
                        title={
                          cScore.status === 'n/a'
                            ? `Cadence too infrequent to score in a 30-day window (${cScore.completed} completed).`
                            : `${cScore.completed} of ${cScore.expected} expected completions in the last 30 days.`
                        }
                      >
                        <Activity className="h-3 w-3" />
                        SLA {cPct !== null ? `${cPct}%` : cStyle.label}
                      </span>
                    )}
                  </div>
                  {!compact && svc.description && (
                    <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                      {svc.description}
                    </p>
                  )}
                  {!compact && svc.policy?.included_scope && (
                    <p className="text-[11px] text-zinc-500 mt-1 italic">
                      Scope: {svc.policy.included_scope}
                    </p>
                  )}
                  {canLogCompletion && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (loggingSlug !== svc.slug) logCompletion(svc.slug, svc.label);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          logCompletion(svc.slug, svc.label);
                        }
                      }}
                      className="inline-flex items-center gap-1 mt-2 rounded-md text-[11px] font-bold uppercase tracking-widest px-2 py-1 border transition"
                      style={{
                        backgroundColor: loggingSlug === svc.slug ? 'rgba(255,214,10,0.15)' : 'rgba(16,185,129,0.12)',
                        borderColor: loggingSlug === svc.slug ? '#FFD60A' : 'rgba(16,185,129,0.5)',
                        color: loggingSlug === svc.slug ? '#FFD60A' : '#34d399',
                      }}
                      data-testid={`contracted-service-log-${svc.slug}`}
                      title={`Log a ${svc.label} completion on this property.`}
                    >
                      <Check className="h-3 w-3" />
                      {loggingSlug === svc.slug ? 'Logging…' : 'Log completion'}
                    </span>
                  )}
                </div>
                {property?.property_id && (
                  <span
                    className="text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-[#C9A962] flex items-center gap-0.5 shrink-0 mt-1"
                    aria-hidden="true"
                  >
                    History
                    <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            </li>
          );})}
        </ul>
      )}

      {property?.property_id && (
        <ServiceHistoryDrawer
          open={Boolean(historySlug)}
          onOpenChange={(v) => { if (!v) setHistorySlug(null); }}
          propertyId={property.property_id}
          propertyName={property.property_name}
          serviceSlug={historySlug}
        />
      )}
    </div>
  );
}
